import React, { useEffect, useState } from 'react';
import { Input, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatQuota, parseQuotaInput } from '../utils/orderQuotaGrid';

interface QuotaCellProps {
    /** 本格自身的值（按日产能或行日产能），未设置时为 null / undefined */
    value: number | null | undefined;
    /** 未设置时灰字展示的沿用值，null 表示未配置 */
    fallback?: number | null;
    readOnly?: boolean;
    /** 是否允许清空（按日产能清空即恢复为行日产能） */
    allowEmpty: boolean;
    /** 不能超过的值（低优上限 ≤ 基础产能） */
    maxValue?: number;
    /** 未配置时是否展示为「不限」：尾程产能管理为 true，分拣产能管理展示为「未设置」；0 表示直接拦截，按数字展示 */
    unsetUnlimited?: boolean;
    /** 输入框尺寸：网格内为 small（默认），HUB 头部的产能输入框为 middle */
    size?: 'small' | 'middle';
    /** 输入框额外样式，如 HUB 头部的加宽、右对齐 */
    inputClassName?: string;
    onCommit: (value: number | null) => Promise<void>;
}

const toText = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));

/**
 * 产能单元格：失焦或回车保存，Esc 撤销；输入非法时标红并提示原因，不保存。
 */
const QuotaCell: React.FC<QuotaCellProps> = ({
    value,
    fallback,
    readOnly,
    allowEmpty,
    maxValue,
    unsetUnlimited = true,
    size = 'small',
    inputClassName,
    onCommit,
}) => {
    const { t } = useTranslation();
    const [text, setText] = useState(toText(value));
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setText(toText(value));
        setError(null);
    }, [value]);

    const label = (quota: number | null): string => {
        if (quota === null) return unsetUnlimited ? t('central.orderQuota.unlimited') : t('central.orderQuota.notSet');
        return formatQuota(quota);
    };
    const placeholder = fallback === undefined ? '' : label(fallback);

    if (readOnly) {
        const shown = value === null || value === undefined ? placeholder : label(value);
        return <span className={value === null || value === undefined ? 'text-gray-400' : 'font-semibold'}>{shown}</span>;
    }

    const commit = async () => {
        if (text === toText(value)) {
            setError(null);
            return;
        }
        const parsed = parseQuotaInput(text, allowEmpty, maxValue);
        if (!parsed.ok) {
            setError(t(`central.orderQuota.validation.${parsed.reason}`, { max: maxValue === undefined ? '' : formatQuota(maxValue) }));
            return;
        }
        setError(null);
        setSaving(true);
        try {
            await onCommit(parsed.value);
        } catch {
            setText(toText(value));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Tooltip title={error} open={!!error}>
            <Input
                size={size}
                value={text}
                disabled={saving}
                status={error ? 'error' : undefined}
                placeholder={placeholder}
                className={[value === null || value === undefined ? '' : 'font-semibold', inputClassName ?? ''].join(' ').trim()}
                onChange={(e) => setText(e.target.value)}
                onBlur={commit}
                onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        setText(toText(value));
                        setError(null);
                    }
                }}
            />
        </Tooltip>
    );
};

export default QuotaCell;
